import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { Calendar } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { guidebooksApi } from '../lib/api';

interface ListingVerificationProps {
  listingCode: string;
  listingName: string;
  onVerified: (token: string) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.625rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '0.875rem',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
};

const dateInputWrapperStyle: React.CSSProperties = {
  position: 'relative',
  cursor: 'pointer',
};

const calendarIconStyle: React.CSSProperties = {
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#9ca3af',
  pointerEvents: 'none',
};

// Style the flatpickr altInput to match our design and fill the wrapper
const FLATPICKR_STYLE = `
  .verification-date-wrapper .flatpickr-input { display: none; }
  .verification-date-wrapper input.flatpickr-alt {
    width: 100%;
    padding: 0.625rem 2.5rem 0.625rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    outline: none;
    box-sizing: border-box;
    background: #fff;
    cursor: pointer;
    color: #1f2937;
  }
  .verification-date-wrapper input.flatpickr-alt::placeholder {
    color: #9ca3af;
  }
  .verification-date-wrapper input.flatpickr-alt:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
  }
`;

export default function ListingVerification({ listingCode, listingName, onVerified }: ListingVerificationProps) {
  const [verificationType, setVerificationType] = useState<'name' | 'contact'>('name');
  const [value, setValue] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const arrivalRef = useRef<HTMLInputElement>(null);
  const departureRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Inject styles for flatpickr altInput
    const style = document.createElement('style');
    style.innerHTML = FLATPICKR_STYLE;
    document.head.appendChild(style);

    if (!arrivalRef.current || !departureRef.current) return;
    const fpConfig = (onChangeFn: (dateStr: string) => void): flatpickr.Options.Options => ({
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'F j, Y',
      altInputClass: 'flatpickr-alt',
      disableMobile: true,
      onChange: (_dates: Date[], dateStr: string) => onChangeFn(dateStr),
    });
    const fpArrival = flatpickr(arrivalRef.current, fpConfig(setArrivalDate));
    const fpDeparture = flatpickr(departureRef.current, fpConfig(setDepartureDate));
    return () => {
      fpArrival.destroy();
      fpDeparture.destroy();
      document.head.removeChild(style);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await guidebooksApi.listingVerify({
        listingCode,
        verificationType,
        value: value.trim(),
        arrivalDate,
        departureDate,
      });
      onVerified(result.token);
    } catch {
      setError('Verification failed. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
          padding: '2rem',
          width: '100%',
          maxWidth: '400px',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.25rem', textAlign: 'center' }}>
          {listingName}
        </h1>
        <p style={{ color: '#64748b', fontSize: '0.875rem', textAlign: 'center', marginBottom: '1.5rem' }}>
          Verify your identity to access the guidebook
        </p>

        {/* Verification type toggle */}
        <div
          style={{
            display: 'flex',
            background: '#f1f5f9',
            borderRadius: '8px',
            padding: '3px',
            marginBottom: '1.25rem',
          }}
        >
          {(['name', 'contact'] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => {
                setVerificationType(type);
                setValue('');
                setError(null);
              }}
              style={{
                flex: 1,
                padding: '0.5rem',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.8125rem',
                fontWeight: 500,
                cursor: 'pointer',
                background: verificationType === type ? '#fff' : 'transparent',
                color: verificationType === type ? '#0f172a' : '#64748b',
                boxShadow: verificationType === type ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              {type === 'name' ? 'Guest Name' : 'Contact Method'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Dynamic field based on verification type */}
          <label style={{ display: 'block', marginBottom: '1rem' }}>
            <span
              style={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#374151',
                display: 'block',
                marginBottom: '0.25rem',
              }}
            >
              {verificationType === 'name' ? 'Guest Name' : 'Email or Phone Number'}
            </span>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={verificationType === 'name' ? 'John Smith' : 'email@example.com or +1234567890'}
              required
              style={inputStyle}
            />
          </label>

          {/* Check-in date */}
          <label style={{ display: 'block', marginBottom: '1rem' }}>
            <span
              style={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#374151',
                display: 'block',
                marginBottom: '0.25rem',
              }}
            >
              Check-in Date
            </span>
            <div className="verification-date-wrapper" style={dateInputWrapperStyle}>
              <input ref={arrivalRef} type="text" placeholder="Select check-in date" readOnly />
              <Calendar size={18} style={calendarIconStyle} />
            </div>
          </label>

          {/* Check-out date */}
          <label style={{ display: 'block', marginBottom: '1.25rem' }}>
            <span
              style={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#374151',
                display: 'block',
                marginBottom: '0.25rem',
              }}
            >
              Check-out Date
            </span>
            <div className="verification-date-wrapper" style={dateInputWrapperStyle}>
              <input ref={departureRef} type="text" placeholder="Select check-out date" readOnly />
              <Calendar size={18} style={calendarIconStyle} />
            </div>
          </label>

          {error && (
            <p style={{ color: '#dc2626', fontSize: '0.8125rem', marginBottom: '1rem', textAlign: 'center' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !value || !arrivalDate || !departureDate}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: loading || !value || !arrivalDate || !departureDate ? '#94a3b8' : '#0f172a',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {loading ? 'Verifying...' : 'Access Guidebook'}
          </button>
        </form>
      </div>
    </div>
  );
}
